<template>
  <div class="editor">
    <!-- Selection Box -->
    <div class="editor-interface" hide="!manifestContent">
      <div class="editor-label">
        <div class="title">{{ label }}</div>
        <div class="attribution">{{ attribution }}</div>
      </div>
      <section class="editor-wrapper">
        <P5
          v-if="p5Data"
          :sketch="sketch"
          :params="p5Data"
          v-on:update:eyes="handleEyeUpdate"
        ></P5>
      </section>
    </div>

    <!-- IIIF Manfest Interface  -->
    <div class="control-interface">
      <label class="iiif-label" for="iiif-source-manifest"
        >Add a IIIF Manifest:
      </label>

      <input
        class="iiif-manifest-input"
        id="iiif-source-manifest"
        v-model.lazy="manifest"
        placeholder="IIIF Manifest URL goes here"
      />
      <div v-if="manifest">
        <label class="thumb-label">Choose a Thumbnail:</label>
        <ul class="thumb-list">
          <li v-for="canvas in canvases" :key="canvas.id">
            <img
              width="50"
              :src="getThumb(canvas)"
              :class="{ selected: canvas == selectedCanvas }"
              @click="selectedCanvas = canvas"
            />
          </li>
        </ul>
      </div>
    </div>
    <template v-if="p5Data">
      <PreviewBox :p5Data="p5Data" :selected="previewType" />

      <AccessBox
        :label="label"
        :gifData="gifData"
        :contentStateUrl="contentStateUrl"
      />
    </template>
  </div>
</template>

<script>
import P5 from "@/components/P5.vue";
import _sketch from "@/libs/sketch.js";
import GIF from "@/libs/gif.min.js";
import { debounce, cloneDeep, compact } from "lodash";
import AccessBox from "@/components/AccessBox.vue";
import PreviewBox from "@/components/PreviewBox.vue";
import { encodeLink, extractManifests } from "content-state";

export default {
  name: "Editor",
  components: {
    P5,
    AccessBox,
    PreviewBox,
  },
  data: function () {
    return {
      selectedCanvas: null,
      manifestContent: null,
      coords: {},
      manifest: null,
      contentStateData: null,
      leftImage: null,
      rightImage: null,
      gifData: null,
    };
  },
  props: {
    contentState: {
      type: Promise,
    },
    previewType: {
      type: String,
    },
  },
  mounted: function () {
    this.contentState.then((c) => {
      this.contentStateData = c;
      this.manifest = extractManifests(c)[0];
    });
  },
  methods: {
    sketch: _sketch,
    getImageID: function (canvas) {
      if (!canvas) return null;
      return canvas.images[0]?.resource?.service["@id"];
    },
    getThumb: function (id) {
      const imageID = this.getImageID(id);
      return `${imageID}/full/,50/0/default.jpg`;
    },
    generateGif: debounce(function () {
      let gif = new GIF({
        workers: 2,
        quality: 10,
      });
      gif.addFrame(this.leftImage.canvas, { delay: 90 });
      gif.addFrame(this.rightImage.canvas, { delay: 90 });
      gif.on("finished", (blob) => {
        this.gifData = blob;
      });
      gif.render();
    }, 500),
    handleEyeUpdate: function (data) {
      this.leftImage = data.l;
      this.rightImage = data.r;
      this.coords = this.computeSegments(data.coords);
      this.gifData = null;
      this.generateGif();
    },
    computeSegments: function (c) {
      const canvasW = this.selectedCanvas.width;
      const canvasH = this.selectedCanvas.height;

      const l = Math.round((canvasW * c.l) / 100);
      const r = Math.round((canvasW * c.r) / 100);
      const y = Math.round((canvasH * c.y) / 100);
      const w = Math.round((canvasW * c.w) / 100);
      const h = Math.round((canvasH * c.h) / 100);

      let seg1 = `xywh=${l},${y},${w},${h}`;
      let seg2 = `xywh=${r},${y},${w},${h}`;

      return [seg1, seg2];
    },
    reloadManifest(url) {
      this.manifestContent = null;
      this.selectedCanvas = null;
      this.leftImage = null;
      this.rightImage = null;
      this.gifData = null;
      var vm = this;
      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          vm.manifestContent = data;
          vm.$nextTick(() => {
            let result;
            if (vm.targetCanvas) {
              result = vm.canvases.find(
                (c) => c.id == vm.targetCanvas || c["@id"] == vm.targetCanvas
              );
            }
            vm.selectedCanvas = result || vm.canvases[0];
          });
        })
        .catch(() => {});
    },
  },
  computed: {
    p5Data: function () {
      if (!this.manifestContent) return null;
      let data = {
        id: this.getImageID(this.selectedCanvas),
        l: this.leftImage,
        r: this.rightImage,
      };
      if (this.targets) {
        data.lcoords = this.targets[0].split("#xywh=")[1];
        data.rcoords = this.targets[1].split("#xywh=")[1];
      }
      if (this.selectedCanvas) {
        data.canvasWidth = this.selectedCanvas.width;
      }
      return data;
    },
    canvases: function () {
      let m = this.manifestContent;
      return m?.sequences[0]?.canvases;
    },
    targets: function () {
      if (!this.contentStateData?.target) return null;
      const target = this.contentStateData.target;
      if (!Array.isArray(target)) {
        if (target.type == "Canvas") {
          return [target.id];
        } else {
          return null;
        }
      }
      return compact(
        target.map((t) => {
          if (t.type == "Canvas") {
            return t.id;
          } else {
            return null;
          }
        })
      );
    },
    label: function () {
      return this.manifestContent?.label || "(no manifest provided)";
    },
    attribution: function () {
      return this.manifestContent?.attribution || "";
    },
    targetCanvas: function () {
      if (!this.targets) return null;
      let canvasId = this.targets[0];
      if (canvasId) {
        canvasId = canvasId.split("#")[0];
      }
      return canvasId;
    },
    contentStateUrl: function () {
      if (!this.selectedCanvas) return null;
      let baseData = {
        type: "Annotation",
        motivation: ["contentState", "stereograph"],
        target: [],
      };
      let baseTarget = {
        id: this.selectedCanvas["@id"] || this.selectedCanvas["id"],
        type: "Canvas",
        partOf: [{ id: this.manifest, type: "Manifest" }],
      };
      baseData.target.push(cloneDeep(baseTarget));
      baseData.target.push(cloneDeep(baseTarget));
      if (this.coords.length) {
        baseData.target[0].id = `${baseData.target[0].id}#${this.coords[0]}`;
        baseData.target[1].id = `${baseData.target[1].id}#${this.coords[1]}`;
      }

      let url = `${document.location.origin}${this.$route.path}`;
      return encodeLink(url, baseData);
    },
  },
  watch: {
    manifest: {
      immediate: true,
      handler: function (newId) {
        console.log(newId);
        this.reloadManifest(newId);
      },
    },
  },
};
</script>

<style scoped lang="scss">
.editor-interface {
  @include interface-box;
  margin-top: 1rem;
  align-self: stretch;
  grid-column: 2 / 4;
}

.title {
  font-weight: bold;
  color: $red;
}
.attribution {
  font-size: 80%;
  color: $red;
  margin-bottom: 0.5rem;
}

.control-interface {
  @include interface-box;
  align-self: stretch;
  grid-column: 2 / 4;
  margin-bottom: 1rem;

  display: grid;
  justify-content: stretch;
  column-gap: 10px;
  row-gap: 2px;
  grid-template-columns: 8rem auto;
  grid-template-rows: min-content min-content;
}

.iiif-manifest-input {
  margin-bottom: 1em;
  box-sizing: border-box;
  font-color: #555;
  font-size: 80%;
  max-height: 1.5rem;
}

.thumb-list {
  list-style: none;
  padding: 0;
  margin: 0;
  li {
    display: inline;
    margin-right: 0.25rem;
    img {
      opacity: 0.5;
      border: 2px solid rgba(255, 255, 255, 0);
    }
    img.selected {
      opacity: 1;
      border: 2px solid $header-color;
    }
  }
}
</style>
